import React from 'react'
import Autosuggest from 'react-autosuggest'
import classNames from '@prague-digi/class-names'
import styles from '@prague-digi/ui-styles'

import { BFormGroup, BFormText, BLabel } from '@prague-digi/ui-components'

export type Suggestions = {
  name: string
  value: any
}

type Props = {
  /** Array of suggestions to display. The only requirement is that suggestions is an array. Items in this array can take an arbitrary shape. */
  data: Array<Suggestions>
  /** Messages to be rendered inside the component. */
  messages: {
    ariaLabel?: string
    error?: string
    label: string
    notice?: string
    placeholder?: string
    warning?: string
  }
  /** This function is called when suggestion is selected. more: https://github.com/moroshko/react-autosuggest#onsuggestionselected-optional */
  onSuggestionSelected: Function
  /** This function will be called every time you might need to update suggestions. More: https://github.com/moroshko/react-autosuggest#onsuggestionsfetchrequested-required */
  onSuggestionsFetchRequested: (searchText: string, fieldName: string) => void
  /** This function will be called every time you need to clear suggestions */
  onSuggestionsClearRequested: () => void
  /** called when the input loses focus, e.g. when user presses Tab */
  onBlur: Function
  /** A callback (event: Object, newValue: string, method: 'up'|'down'|'escape'|'enter'|'click'|'type') for onChange event. */
  onChange: Function
  name: string
  /** A value passed from a store or redux-forms. So if you need an init value, prepare it on your side/component and pass it in this "value" property. */
  value?: String
}

// When suggestion is clicked, Autosuggest needs to populate the input
// based on the clicked suggestion. Teach Autosuggest how to calculate the
// input value for every given suggestion.
const getSuggestionValue = (suggestion: Suggestions): string => suggestion.name

// Use your imagination to render suggestions.
const renderSuggestion = (suggestion: Suggestions): string => suggestion.name

const cx = classNames.bind([styles]) // eslint-disable-line react/jsx-no-bind

const theme = {
  input: cx('form-control'),
  suggestionsContainerOpen: cx('dropdown-menu', 'show', 'p-0'),
  suggestionsList: cx('p-0', 'm-0'),
  suggestion: cx('dropdown-item'),
  suggestionHighlighted: cx('bg-light'),
}

export const PureAutoComplete = ({
  data,
  messages,
  onBlur,
  onChange,
  onSuggestionsClearRequested,
  name,
  onSuggestionSelected,
  onSuggestionsFetchRequested,
  value,
}: Props) => {
  const { error, label, notice, placeholder, warning } = messages

  // onSuggestionSelected callback is called before onChange callback. That's why this middleware callback just prepare
  // flag and the onSuggestionSelected from props is called in the local onChange callback.
  const handleSuggestionSelected = (event: any, params: any) => {
    onSuggestionSelected(params.suggestion)
  }

  // Autosuggest will pass through all these props to the input.
  const inputProps = {
    name,
    onBlur,
    onChange,
    placeholder: placeholder || '',
    value: value || '',
  }

  return (
    <BFormGroup error={!!error} warning={!!warning} theme={theme}>
      <BLabel>{label}</BLabel>

      <Autosuggest
        suggestions={data}
        onSuggestionsFetchRequested={(data: any) => {
          onSuggestionsFetchRequested(data.value, name)
        }}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={handleSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        // @ts-ignore
        inputProps={inputProps}
        theme={theme}
      />

      {!!notice && <BFormText>{notice}</BFormText>}
      {!!error && <BFormText>{error}</BFormText>}
      {!!warning && <BFormText>{warning}</BFormText>}
    </BFormGroup>
  )
}

export default PureAutoComplete
